import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import appStore from '@/store/app';
import ordersStore from '@/store/orders';
import productsStore from '@/store/products';
import CardComponent from '@/components/CardComponent';
import ListToolbar from '@/components/ListToolbar';
import ColumnStatus from '@/components/ColumnStatus';
import { Product, ProductStatus, TypeOfSearch, BaseProduct, TypeOfOperation } from '@/types';
import { debounce } from '@/core/debounce';
import { ProductsSearch } from '@/models';
// import tracer from '@/core/tracer';

@Component({
   components: { CardComponent, ListToolbar, ColumnStatus },
})
export default class Products extends Vue {

   private ProductStatus: typeof ProductStatus = ProductStatus;

   @Prop({ default: true }) private readonly checkable!: boolean;

   private selected: Product | null = null;

   private keyword = this.search.keyword;
   private searchType = this.search.searchType;
   private searchInHistorical = this.search.searchInHistorical;
   private currentPage = this.search.pageNumber;
   private pageSize = this.search.pageSize;
   private searching = false;

   private readonly minLength = 3;

   private debouncedFetchProductsAsync = async (): Promise<void> => undefined;

   get loading(): boolean {
      return appStore.isLoading;
   }

   get products(): Product[] {
      return productsStore.products;
   }

   get total(): number {
      return productsStore.totalProducts;
   }

   get search(): ProductsSearch {
      return productsStore.search;
   }

   private async created() {
      this.debouncedFetchProductsAsync = debounce(this.fetchProductsAsync, 680);
      if (!this.products || this.total === 0) {
         await this.fetchProductsAsync();
      }
   }

   private async mounted() {
      await appStore.activateMenuById({ rootId: 100, subMenuId: 101 });
   }

   @Watch('keyword', { immediate: false, deep: false })
   private async onKeywordChanged(value: string, previousValue?: string) {

      // tracer('onKeywordChanged', [value, previousValue]);

      if (previousValue === undefined) return;

      if (value.trim() === '' || value.trim().length >= this.minLength) {
         this.search.keyword = value;
         this.search.pageNumber = 1;
         await this.debouncedFetchProductsAsync();
      }
   }

   @Watch('searchInHistorical', { immediate: false, deep: false })
   private async onHistoricalChanged(value: boolean, previousValue?: boolean) {
      // tracer('onHistoricalChanged', [value, previousValue]);
      if (previousValue === undefined) return;
      this.search.pageNumber = 1;
      this.search.searchInHistorical = value;
      await this.debouncedFetchProductsAsync();
   }

   @Watch('searchType', { immediate: false, deep: false })
   private async onSearchTypeChanged(value: TypeOfSearch, previousValue?: TypeOfSearch) {
      // tracer('onSearchTypeChanged', [value, previousValue]);
      if (previousValue === undefined) return;
      this.search.searchType = value;
      this.search.pageNumber = 1;
      await this.debouncedFetchProductsAsync();
   }

   private async onSort(field: string, direction: string) {
      this.search.sortField = field;
      this.search.sortDirection = direction;
      await this.fetchProductsAsync();
   }

   private async onPageChange(page: number) {
      this.search.pageNumber = page;
      await this.fetchProductsAsync();
   }

   private select(row: Product, oldRow: Product | null): void {
      this.selected = row;
   }

   private async goToProduct(product: Product) {
      if (product) {
         await this.$router.push({ name: 'product', params: { type: product.type, pharmacode: product.pharmacode } });
      }
   }

   private async addToCartWithQuantity(product: Product, addToHistorical: boolean) {
      if (product && product.type && product.pharmacode) {

         this.$buefy.dialog.prompt({
            confirmText: 'Conferma',
            cancelText: 'Annulla',
            type: 'is-info',
            title: `${product.pharmacode} ${product.name || ''}`,
            message: `Quantità ?`,
            inputAttrs: {
                type: 'number',
                placeholder: 'Inserisci Quantità',
                value: '1',
                maxlength: 3,
                min: 1,
                max: 999,
            },
            trapFocus: true,
            onConfirm: async (value: string) => await this.addToCartQuantity(product, addToHistorical, parseInt(value, 0)),
        });
      }
   }

   private async addToCart(product: Product, addToHistorical: boolean) {
      if (product && product.type && product.pharmacode) {
         await ordersStore.addProductToCartAsync({ product: product as BaseProduct, quantity: 1, operation: TypeOfOperation.AddQuantity, addToHistorical })
                          .then(() => {
                              appStore.info({ message: `Prodotto ${product.pharmacode} - ${product.name} aggiunto al carrello.`, timeout: 1000 });
                           });
      }
   }

   private async addToCartQuantity(product: Product, addToHistorical: boolean, quantity: number) {
      if (product && product.type && product.pharmacode) {
         await ordersStore.addProductToCartAsync({ product: product as BaseProduct, quantity, operation: TypeOfOperation.AddQuantity, addToHistorical })
                          .then(() => {
                              appStore.info({ message: `Prodotto ${product.pharmacode} - ${product.name} aggiunto al carrello.`, timeout: 1000 });
                           });
      }
   }

   private async reload() {
      this.search.pageNumber = 1;
      await this.fetchProductsAsync();
   }

   private async fetchProductsAsync() {
      // tracer('fetchProductsAsync', this.search);
      let counter = 0;
      while (this.searching) {
         counter++;
         await this.delay(200);
         if (counter > 5) {
            this.searching = false;
         }
      }
      this.searching = true;
      await productsStore.fetchProductsAsync(this.search)
               .finally(() => {
                  this.searching = false;
               });
   }

   private delay(ms: number) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

}
